import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import api from "@/lib/api";
import { MODULES, EVENT_CATEGORIES } from "@/lib/constants";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Check, ArrowRight, ArrowLeft, Upload } from "lucide-react";
import { toast } from "sonner";
import { SonarGrid } from "@/components/ui/sonar-grid";

const TEAM_SIZES = ["Solo", "2-5", "6-15", "16+"];
const VOLUMES = ["< 20", "20-50", "50-150", "150+"];
const PLANS = [
  { key: "starter", label: "Starter", price: 29, tagline: "Pour démarrer en solo" },
  { key: "pro", label: "Pro", price: 79, tagline: "Le plus populaire" },
  { key: "agence", label: "Agence", price: 199, tagline: "Pour les grandes équipes" },
];

export default function Onboarding() {
  const { org, refreshOrg, user } = useAuth();
  const { theme } = useTheme();
  const brandLogo = theme === "dark" ? "/Logo_Full_White.webp" : "/Logo_Full_Black.webp";
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: org?.name || "",
    logo: org?.logo || "",
    brand_color: org?.brand_color || "#FF6A00",
    brand_website: org?.brand_website || "",
    brand_tagline: org?.brand_tagline || "",
    contact_email: org?.contact_email || user?.email || "",
    phone: org?.phone || "",
    address: org?.address || "",
    address_coords: org?.address_coords || null,
    country: org?.country || "France",
    fiscal_status: org?.fiscal_status || "auto-entrepreneur",
    siret: org?.siret || "",
    vat_number: org?.vat_number || "",
    share_capital: org?.share_capital || "",
    vat_registered: org?.vat_registered ?? false,
    tax_rate_default: org?.tax_rate_default ?? 20,
    modules: org?.modules || [],
    event_types: org?.event_types || [],
    team_size: org?.team_size || "Solo",
    events_per_year: org?.events_per_year || "< 20",
    deposit_percent: org?.deposit_percent ?? 30,
    balance_days_before: org?.balance_days_before ?? 7,
    cancellation_policy: org?.cancellation_policy || "",
    legal_mentions: org?.legal_mentions || "",
    iban: org?.iban || "",
    bic: org?.bic || "",
    plan: org?.billing?.plan || "",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (key, k) => setForm((f) => ({ ...f, [key]: f[key].includes(k) ? f[key].filter((m) => m !== k) : [...f[key], k] }));
  const isSociete = form.fiscal_status === "societe";

  const handleLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const r = new FileReader();
    r.onload = () => set("logo", r.result);
    r.readAsDataURL(file);
  };

  const steps = ["Identité & marque", "Coordonnées", "Infos légales", "Activité", "Commercial & paiement", "Formule", "Récapitulatif"];

  const next = () => {
    if (step === 1 && !form.name.trim()) return toast.error("Le nom commercial est requis");
    if (step === 2 && !form.contact_email.trim()) return toast.error("L'email de contact est requis");
    if (step === 6 && !form.plan) return toast.error("Choisissez une formule pour continuer");
    setStep((s) => Math.min(s + 1, steps.length));
  };

  const finish = async () => {
    if (!form.name.trim() || !form.contact_email.trim()) { toast.error("Nom et email de contact requis"); return; }
    setSaving(true);
    try {
      await api.put("/org", { ...form, onboarding_done: true, onboarding_step: steps.length });
      if (form.plan) { try { await api.post("/subscription/select-plan", { plan: form.plan }); } catch (err) { /* noop */ } }
      try { const me = await api.get("/auth/me"); refreshOrg(me.data.org); } catch (err) { /* noop */ }
      toast.success("Configuration terminée — bienvenue sur HalgarOS !");
      navigate("/app");
    } catch (e) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const Chip = ({ active, onClick, testid, children }) => (
    <button type="button" data-testid={testid} onClick={onClick}
      className={`rounded-full border px-4 py-2 text-sm font-medium transition-all ${active ? "border-[#FF6A00] bg-[#FF6A00]/10 text-foreground" : "border-border bg-muted text-muted-foreground hover:border-border"}`}>
      {children}
    </button>
  );

  return (
    <SonarGrid
      gradient={["#FF6A00", "#FFD500"]}
      className="min-h-screen bg-background px-4 py-10"
    >
      <div className="relative z-10 mx-auto max-w-2xl">
        <div className="mb-8 text-center">
          <img src={brandLogo} alt="HalgarOS" className="mx-auto mb-5 h-12 w-auto" />
          <h1 className="font-display text-3xl font-bold text-foreground">Configurons votre espace</h1>
          <p className="mt-2 text-muted-foreground">Chaque réponse configure réellement votre application.</p>
        </div>

        <div className="mb-8 flex items-center justify-center gap-1.5">
          {steps.map((s, i) => (
            <div key={s} className="flex items-center gap-1.5">
              <div className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold ${i + 1 <= step ? "bg-[#FF6A00] text-foreground" : "bg-muted text-muted-foreground"}`}>
                {i + 1 < step ? <Check className="h-4 w-4" /> : i + 1}
              </div>
              {i < steps.length - 1 && <div className={`h-0.5 w-6 ${i + 1 < step ? "bg-[#FF6A00]" : "bg-muted"}`} />}
            </div>
          ))}
        </div>

        <div className="rounded-3xl border border-border bg-card/90 p-8 shadow-2xl backdrop-blur-xl">
          {step === 1 && (
            <div className="space-y-5">
              <h2 className="font-display text-xl font-semibold text-foreground">Identité & marque</h2>
              <div>
                <Label className="text-muted-foreground">Nom commercial *</Label>
                <Input data-testid="onb-name" className="mt-1.5 border-border bg-muted" value={form.name} onChange={(e) => set("name", e.target.value)} />
              </div>
              <div>
                <Label className="text-muted-foreground">Logo</Label>
                <div className="mt-1.5 flex items-center gap-3">
                  <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-xl border border-border bg-muted">
                    {form.logo ? <img data-testid="onb-logo-preview" src={form.logo} alt="logo" className="max-h-14 max-w-14 object-contain" /> : <span className="text-2xl font-bold text-muted-foreground">{(form.name || "E").slice(0, 1)}</span>}
                  </div>
                  <label className="cursor-pointer rounded-full border border-border px-3 py-2 text-sm text-foreground hover:bg-muted">
                    <input data-testid="onb-logo-input" type="file" accept="image/*" className="hidden" onChange={handleLogo} />
                    <span className="flex items-center gap-1.5"><Upload className="h-4 w-4" />{form.logo ? "Changer" : "Choisir un logo"}</span>
                  </label>
                  {form.logo && <button onClick={() => set("logo", "")} className="text-xs text-muted-foreground hover:text-red-400">Retirer</button>}
                </div>
              </div>
              <div>
                <Label className="text-muted-foreground">Couleur de marque</Label>
                <div className="mt-1.5 flex items-center gap-3">
                  <input type="color" data-testid="onb-color" value={form.brand_color} onChange={(e) => set("brand_color", e.target.value)} className="h-10 w-16 cursor-pointer rounded-lg border border-border bg-muted" />
                  <span className="text-sm text-muted-foreground">{form.brand_color}</span>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <Label className="text-muted-foreground">Site web</Label>
                  <Input data-testid="onb-website" placeholder="https://…" className="mt-1.5 border-border bg-muted" value={form.brand_website} onChange={(e) => set("brand_website", e.target.value)} />
                </div>
                <div>
                  <Label className="text-muted-foreground">Slogan</Label>
                  <Input data-testid="onb-tagline" placeholder="Votre accroche" className="mt-1.5 border-border bg-muted" value={form.brand_tagline} onChange={(e) => set("brand_tagline", e.target.value)} />
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <h2 className="font-display text-xl font-semibold text-foreground">Coordonnées</h2>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <Label className="text-muted-foreground">Email de contact *</Label>
                  <Input data-testid="onb-email" type="email" className="mt-1.5 border-border bg-muted" value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} />
                </div>
                <div>
                  <Label className="text-muted-foreground">Téléphone</Label>
                  <Input data-testid="onb-phone" className="mt-1.5 border-border bg-muted" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
                </div>
              </div>
              <div>
                <Label className="text-muted-foreground">Adresse</Label>
                <div className="mt-1.5">
                  <AddressAutocomplete testid="onb-address" value={form.address} className="border-border bg-muted"
                    onChange={(v) => set("address", v)} onSelect={(d) => set("address_coords", d.coordinates || null)} />
                </div>
              </div>
              <div>
                <Label className="text-muted-foreground">Pays</Label>
                <Input data-testid="onb-country" className="mt-1.5 border-border bg-muted" value={form.country} onChange={(e) => set("country", e.target.value)} />
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5">
              <h2 className="font-display text-xl font-semibold text-foreground">Infos légales</h2>
              <div className="grid grid-cols-2 gap-3">
                {["auto-entrepreneur", "societe"].map((s) => (
                  <button key={s} type="button" data-testid={`onb-fiscal-${s}`} onClick={() => set("fiscal_status", s)}
                    className={`rounded-2xl border p-4 text-sm font-medium transition-all ${form.fiscal_status === s ? "border-[#FF6A00] bg-[#FF6A00]/10 text-foreground" : "border-border bg-muted text-muted-foreground"}`}>
                    {s === "societe" ? "Société" : "Auto-entrepreneur"}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <Label className="text-muted-foreground">SIRET</Label>
                  <Input data-testid="onb-siret" className="mt-1.5 border-border bg-muted" value={form.siret} onChange={(e) => set("siret", e.target.value)} />
                </div>
                <div>
                  <Label className="text-muted-foreground">N° TVA</Label>
                  <Input data-testid="onb-vat" className="mt-1.5 border-border bg-muted" value={form.vat_number} onChange={(e) => set("vat_number", e.target.value)} />
                </div>
              </div>
              {isSociete && (
                <div>
                  <Label className="text-muted-foreground">Capital social</Label>
                  <Input data-testid="onb-capital" placeholder="ex : 10 000 €" className="mt-1.5 border-border bg-muted" value={form.share_capital} onChange={(e) => set("share_capital", e.target.value)} />
                </div>
              )}
              <div className="rounded-2xl border border-border bg-muted p-4">
                <label className="flex cursor-pointer items-center justify-between text-sm text-foreground">
                  <span>Assujetti à la TVA ?</span>
                  <input type="checkbox" data-testid="onb-vat-registered" checked={form.vat_registered} onChange={(e) => set("vat_registered", e.target.checked)} className="h-4 w-4 accent-[#FF6A00]" />
                </label>
                {form.vat_registered && (
                  <div className="mt-3">
                    <Label className="text-muted-foreground">Taux de TVA par défaut (%)</Label>
                    <Input type="number" data-testid="onb-tax-rate" className="mt-1.5 border-border bg-muted" value={form.tax_rate_default} onChange={(e) => set("tax_rate_default", parseFloat(e.target.value) || 0)} />
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-6">
              <h2 className="font-display text-xl font-semibold text-foreground">Votre activité</h2>
              <div>
                <Label className="text-muted-foreground">Métier(s) exercé(s)</Label>
                <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {MODULES.map((m) => {
                    const active = form.modules.includes(m.key);
                    const soon = (org?.access?.modules_soon || []).includes(m.key);
                    return (
                      <button key={m.key} type="button" data-testid={`onb-module-${m.key}`} disabled={soon} onClick={() => toggle("modules", m.key)}
                        className={`flex flex-col items-center gap-2 rounded-2xl border p-3 text-center text-xs transition-all ${soon ? "cursor-not-allowed border-dashed border-border bg-muted/40 text-muted-foreground" : active ? "border-[#FF6A00] bg-[#FF6A00]/10 text-foreground" : "border-border bg-muted text-muted-foreground hover:border-border"}`}>
                        <m.icon className={`h-5 w-5 ${active && !soon ? "text-[#FF6A00]" : ""}`} strokeWidth={1.6} />
                        {m.label}
                        {soon && <span data-testid={`onb-module-soon-${m.key}`} className="soon-badge rounded-full px-2 py-0.5 text-[10px] font-medium">Disponible bientôt</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label className="text-muted-foreground">Types d'événements gérés</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {EVENT_CATEGORIES.map((c) => <Chip key={c} testid={`onb-evtype-${c}`} active={form.event_types.includes(c)} onClick={() => toggle("event_types", c)}>{c}</Chip>)}
                </div>
              </div>
              <div>
                <Label className="text-muted-foreground">Taille de l'équipe</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {TEAM_SIZES.map((t) => <Chip key={t} testid={`onb-team-${t}`} active={form.team_size === t} onClick={() => set("team_size", t)}>{t}</Chip>)}
                </div>
              </div>
              <div>
                <Label className="text-muted-foreground">Événements par an</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {VOLUMES.map((v) => <Chip key={v} testid={`onb-vol-${v}`} active={form.events_per_year === v} onClick={() => set("events_per_year", v)}>{v}</Chip>)}
                </div>
              </div>
            </div>
          )}

          {step === 5 && (
            <div className="space-y-6">
              <h2 className="font-display text-xl font-semibold text-foreground">Commercial & paiement</h2>
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-muted-foreground">Pourcentage d'acompte</Label>
                  <span className="font-semibold text-[#FF6A00]">{form.deposit_percent}%</span>
                </div>
                <Slider className="mt-3" value={[form.deposit_percent]} min={0} max={100} step={5} onValueChange={(v) => set("deposit_percent", v[0])} />
              </div>
              <div>
                <Label className="text-muted-foreground">Délai de solde avant l'événement (jours)</Label>
                <Input type="number" data-testid="onb-balance-days" className="mt-1.5 border-border bg-muted" value={form.balance_days_before} onChange={(e) => set("balance_days_before", parseInt(e.target.value) || 0)} />
              </div>
              <div>
                <Label className="text-muted-foreground">Politique d'annulation</Label>
                <Textarea data-testid="onb-cancellation" className="mt-1.5 border-border bg-muted" rows={2} value={form.cancellation_policy} onChange={(e) => set("cancellation_policy", e.target.value)} />
              </div>
              <div>
                <Label className="text-muted-foreground">Mentions légales par défaut</Label>
                <Textarea data-testid="onb-legal" className="mt-1.5 border-border bg-muted" rows={2} value={form.legal_mentions} onChange={(e) => set("legal_mentions", e.target.value)} />
              </div>
            </div>
          )}

          {step === 6 && (
            <div className="space-y-5">
              <h2 className="font-display text-xl font-semibold text-foreground">Choisissez votre formule</h2>
              <p className="text-sm text-muted-foreground">14 jours d'essai gratuit — vous ne payez qu'à la fin de la période d'essai. Modifiable à tout moment.</p>
              <div className="grid gap-3 sm:grid-cols-3">
                {PLANS.map((p) => {
                  const active = form.plan === p.key;
                  return (
                    <button key={p.key} type="button" data-testid={`onb-plan-${p.key}`} onClick={() => set("plan", p.key)}
                      className={`flex flex-col rounded-2xl border p-4 text-left transition-all ${active ? "border-[#FF6A00] bg-[#FF6A00]/10" : "border-border bg-muted hover:border-border"}`}>
                      <span className="font-display text-base font-bold text-foreground">{p.label}</span>
                      <span className="mt-1"><span className="font-display text-2xl font-bold text-foreground">{p.price} €</span><span className="text-xs text-muted-foreground"> /mois</span></span>
                      <span className="mt-1 text-xs text-muted-foreground">{p.tagline}</span>
                      {active && <span className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-[#FF6A00]"><Check className="h-3.5 w-3.5" />Sélectionné</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 7 && (
            <div className="space-y-4 text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#FF6A00]/15">
                <Check className="h-8 w-8 text-[#FF6A00]" />
              </div>
              <h2 className="font-display text-xl font-semibold text-foreground">Tout est prêt, {user?.name} !</h2>
              <div className="mx-auto max-w-md space-y-1 text-left text-sm text-muted-foreground">
                <p><span className="text-muted-foreground">Entreprise :</span> {form.name || "—"} {form.brand_tagline && `· ${form.brand_tagline}`}</p>
                <p><span className="text-muted-foreground">Contact :</span> {form.contact_email}{form.phone && ` · ${form.phone}`}</p>
                <p><span className="text-muted-foreground">Statut :</span> {isSociete ? "Société" : "Auto-entrepreneur"}{form.siret && ` · SIRET ${form.siret}`}{form.vat_registered ? ` · TVA ${form.tax_rate_default}%` : " · Non assujetti TVA"}</p>
                <p><span className="text-muted-foreground">Activité :</span> {form.modules.length} module(s), {form.event_types.length} type(s) d'événement, équipe {form.team_size}</p>
                <p><span className="text-muted-foreground">Commercial :</span> acompte {form.deposit_percent}%, solde J-{form.balance_days_before}</p>
                <p><span className="text-muted-foreground">Formule :</span> {form.plan ? `${form.plan.charAt(0).toUpperCase()}${form.plan.slice(1)} — essai gratuit 14 jours` : "—"}</p>
              </div>
            </div>
          )}

          <div className="mt-8 flex items-center justify-between">
            <Button variant="ghost" disabled={step === 1} onClick={() => setStep(step - 1)} className="text-muted-foreground hover:text-foreground">
              <ArrowLeft className="mr-1 h-4 w-4" /> Retour
            </Button>
            {step < steps.length ? (
              <Button data-testid="onb-next" onClick={next} className="rounded-full bg-[#FF6A00] font-semibold text-foreground hover:bg-[#E65F00]">
                Continuer <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            ) : (
              <Button data-testid="onb-finish" onClick={finish} disabled={saving} className="rounded-full bg-[#FF6A00] font-semibold text-foreground hover:bg-[#E65F00]">
                {saving ? "…" : "Accéder à mon espace"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </SonarGrid>
  );
}
